import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { map } from "rxjs/operators";

import { environment } from "src/environments/environment";
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

const BACKEND_URL = environment.apiUrl + "/files/";

@Injectable({
    providedIn: "root"
})

export class FilesService {
    private files: any[] = [];

    constructor(
        private http: HttpClient,
    ) { }

    // [plotter]
    getUserFiles(
        userId: string,
        printerId: string
    ): Observable<{ files: any[]; plotterUploadErrors?: any[] }> {
        return this.http
            .get<{ message: string; files: any[]; plotterUploadErrors?: any[] }>(`${BACKEND_URL}/filesforuser/${printerId}/${userId}`)
            .pipe(
                map(filesData => ({
                    files: [...(filesData.files || [])],
                    plotterUploadErrors: filesData.plotterUploadErrors || []
                }))
            );
    }

    // [express]
    getUserFilesExpress(
        userId: string,
        singlePaper: string | null = null,
        printerID: string | null = null
    ): Observable<{ message: string; files: any[]; refreshTable?: boolean; printer?: any; expressUploadErrors?: any[] }> {
        let url: string;
        
        // Build URL based on whether we have singlePaper and printerID
        if (singlePaper && printerID && singlePaper !== '' && printerID !== '') {
            // Both singlePaper and printerID exist - use path parameters
            url = `${BACKEND_URL}/filesforuserexpress/${userId}/${singlePaper}/${printerID}`;
        } else if (printerID && printerID !== '') {
            // Only printerID exists - send it as query parameter
            url = `${BACKEND_URL}/filesforuserexpress/${userId}?printerID=${printerID}`;
        } else {
            // No printerID - use basic URL
            url = `${BACKEND_URL}/filesforuserexpress/${userId}`;
        }
        
        return this.http
            .get<{ message: string; files: any[]; refreshTable?: boolean; printer?: any; expressUploadErrors?: any[] }>(url);
    }

    // [plotter]
    updateFileSettings(
        fileId: string,
        imageId: number,
        newPrintSettings: object
    ): Observable<any> {
        const url = `${BACKEND_URL}/updatefilesettings/${fileId}`;
        const body = { printSettings: newPrintSettings, imageId: imageId };
        return this.http.put(url, body);
    }

    // [express]
    updateFileSettingsExpress(
        fileId: string,
        newPrintSettings: object,
        printerID: string,
    ): Observable<any> {
        const url = `${BACKEND_URL}/updatefilesettingsexpress/${fileId}`;
        const body = { printSettings: newPrintSettings, printerID: printerID };
        return this.http.put(url, body);
    }

    // [express]
    clearFileSettingsExpress(
        userId: string,
    ): Observable<any> {
        const url = `${BACKEND_URL}/clearfilesettingsexpress/${userId}`;
        const body = {};
        return this.http.put(url, body);
    }

    // [plotter]
    deleteFile(currentFile: any, serverAddress: string) {
        const url = serverAddress + `/api/files/delete/${currentFile._id}`;
        return this.http.delete(url);
    }
    // [express]
    deleteFileExpress(currentFile: any, serverAddress: string) {
        const url = serverAddress + `/api/files/delete/${currentFile._id}`;
        return this.http.delete(url);
    }

    // [plotter]
    deleteImage(currentFile: any, currentImage: any, serverAddress: string) {
        const url = serverAddress + `/api/files/delete/${currentFile._id}/${currentImage._id}`;
        return this.http.delete(url);
    }
    // [express]
    deleteImageExpress(currentFile: any, currentImage: any, serverAddress: string) {
        const url = serverAddress + `/api/files/delete/${currentFile._id}/${currentImage._id}`;
        return this.http.delete(url);
    }

    // [plotter]
    deleteAllFiles(userId: string, branchId: string) {
        const url = `${BACKEND_URL}/deleteallfiles/${userId}/${branchId}`;
        return this.http.delete(url);
    }
    // [express]
    deleteAllFilesExpress(userId: string) {
        const url = `${BACKEND_URL}/deleteallfilesexpress/${userId}`;
        return this.http.delete(url);
    }

    // [plotter]
    applyToAll(
        printerId: string,
        newPrintSettings: object
    ): Observable<any> {
        const userId = localStorage.getItem("userId");
        const body = { printSettings: newPrintSettings };
        const url = `${BACKEND_URL}/applytoall/${userId}/${printerId}`;
        return this.http.put(url, body).pipe(
            tap(updatedFiles => {
                // Assuming this.files is your local state that holds the files
                this.files = updatedFiles;
            })
        );
    }
    // [express]
    applyToAllExpress(
        newPrintSettings: object,
        printerID: string,
    ): Observable<any> {
        const userId = localStorage.getItem("userId");
        const body = { printSettings: newPrintSettings, printerID: printerID };
        const url = `${BACKEND_URL}/applytoallexpress/${userId}`;
        return this.http.put(url, body).pipe(
            tap(updatedFiles => {
                this.files = updatedFiles;
            })
        );
    }

    // [plotter]
    updateResizeError(
        fileId: string,
        imageIndex: number,
    ): Observable<any> {
        const url = `${BACKEND_URL}/updateresizeerror/${fileId}/${imageIndex}`;
        return this.http.put(url, {});
    }

    // Transfer files to user
    transferFilesToUser(
        printingService: string,
        printerOrBranchID: string,
        targetUser: string,
        fileIds: string[],
        currentUserId: string
    ): Observable<any> {
        const url = `${BACKEND_URL}transferfilestouser`;
        const body = {
            printingService: printingService,
            printerOrBranchID: printerOrBranchID,
            targetUser: targetUser,
            fileIds: fileIds,
            currentUserId: currentUserId
        };
        return this.http.post(url, body);
    }

}
